import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight, CheckCircle2, Shield, Brain, FileText, Clock,
  TrendingUp, Users, Zap, AlertTriangle
} from 'lucide-react';

const AIConsultingInsurance = () => {
  const useCases = [
    {
      icon: AlertTriangle,
      title: 'Claims Automation & Triage',
      description: 'AI systems that automatically classify, triage, and route incoming claims reducing manual handling and accelerating settlements for straightforward claims.',
      outcomes: ['60% reduction in manual claims handling', 'Straight-through processing for low-complexity claims', 'Fraud signal detection at intake'],
    },
    {
      icon: Shield,
      title: 'Fraud Detection & Anomaly Scoring',
      description: 'Real-time ML models that score every claim and transaction for fraud signals, detecting organized rings, medical billing fraud, and policy abuse.',
      outcomes: ['95%+ fraud detection accuracy', 'Real-time scoring at intake', '$3M+ in prevented payouts (client result)'],
    },
    {
      icon: Brain,
      title: 'Underwriting AI & Risk Scoring',
      description: 'ML-powered risk assessment that ingests structured and unstructured data to generate accurate risk scores and premium recommendations faster than manual underwriting.',
      outcomes: ['80% faster underwriting decisions', 'Consistent risk scoring across all submissions', 'Third-party data enrichment integration'],
    },
    {
      icon: FileText,
      title: 'Policy Document Intelligence',
      description: 'NLP systems that extract key terms, exclusions, and coverage limits from policy documents enabling automated comparison, compliance checks, and Q&A bots.',
      outcomes: ['Automated policy Q&A chatbot', 'Coverage gap detection', '90% faster policy data extraction'],
    },
    {
      icon: Users,
      title: 'Customer Service AI & Self-Service',
      description: 'AI-powered chatbots and virtual agents that handle policy inquiries, FNOL (First Notice of Loss) intake, and claims status available 24/7.',
      outcomes: ['40% reduction in call center volume', 'FNOL intake without agent involvement', 'Claims status self-service'],
    },
    {
      icon: TrendingUp,
      title: 'Actuarial & Predictive Modeling',
      description: 'ML-augmented actuarial models for loss reserving, lapse prediction, and lifetime value modeling that go beyond traditional GLM approaches.',
      outcomes: ['More accurate loss reserves', 'Churn/lapse prediction for retention', 'Customer LTV segmentation'],
    },
  ];

  const caseStudy = {
    title: 'Insurance Claims Automation Payment Processing',
    result: 'Automated end-to-end payment processing for an insurance carrier, reducing settlement time from 14 days to under 48 hours for 70% of claims.',
    link: '/case-studies/payment-automation',
  };

  return (
    <>
      <Helmet>
        <title>AI Consulting for Insurance | Claims Automation & Fraud Detection AI | Agility</title>
        <meta
          name="description"
          content="Insurance AI consulting specialists. Claims automation, fraud detection ML, underwriting AI, policy document intelligence, and customer service bots. Production AI in 3-8 weeks for P&C, life, health, and specialty insurers."
        />
        <meta
          name="keywords"
          content="AI consulting insurance, insurance AI consulting, claims automation AI, fraud detection insurance AI, underwriting AI, insurance machine learning, insurance NLP, InsurTech AI consulting, P&C insurance AI, AI for insurance companies"
        />
        <link rel="canonical" href="https://www.agilitytech.ai/ai-consulting-insurance" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="AI Consulting for Insurance | Claims & Fraud Detection AI | Agility" />
        <meta property="og:description" content="Insurance AI consulting: claims automation, fraud detection, underwriting AI, policy intelligence. Production AI in 3-8 weeks." />
        <meta property="og:url" content="https://www.agilitytech.ai/ai-consulting-insurance" />
        <meta property="og:image" content="https://www.agilitytech.ai/agility hex fonts.png" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Insurance AI Consulting",
            "provider": { "@type": "Organization", "name": "Agility", "url": "https://www.agilitytech.ai" },
            "description": "AI consulting for insurance companies. Claims automation, fraud detection ML, underwriting AI, and customer service automation for P&C, life, and specialty insurers.",
            "areaServed": "Worldwide",
            "serviceType": "Insurance AI Consulting"
          })}
        </script>
      </Helmet>

      {/* Hero */}
      <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-indigo-900 via-black to-teal-900">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-500/10 border border-indigo-500/30 rounded-full mb-8">
            <Shield className="w-4 h-4 text-indigo-400" />
            <span className="text-indigo-300 text-sm font-medium">Insurance AI Consulting</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            AI Consulting for<br />
            <span className="text-teal-400">Insurance</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-10 leading-relaxed">
            Claims automation, fraud detection, underwriting AI, and policy intelligence for P&C, life, health,
            and specialty insurers. Production-ready systems in 3-8 weeks.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Link
              to="/contact"
              className="group inline-flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold transition-all"
            >
              Get an Insurance AI Consultation
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/case-studies/insurance-automation"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-lg font-semibold border border-white/20 transition-all"
            >
              See Insurance Case Study
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {[
              { value: '3-8 Weeks', label: 'To Production', desc: 'Claims or underwriting AI live' },
              { value: '95%+', label: 'Fraud Accuracy', desc: 'ML fraud detection models' },
              { value: '60%', label: 'Manual Reduction', desc: 'In claims handling effort' },
              { value: '$3M+', label: 'Claims Saved', desc: 'From fraud detection alone' },
            ].map((m, i) => (
              <div key={i} className="text-center">
                <div className="text-4xl font-bold text-teal-400 mb-1">{m.value}</div>
                <div className="text-white font-semibold mb-1">{m.label}</div>
                <div className="text-gray-400 text-sm">{m.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Insurance AI Use Cases We Deliver</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              High-value, high-ROI AI implementations built around insurance workflows not generic ML demos.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {useCases.map((u, i) => {
              const Icon = u.icon;
              return (
                <div key={i} className="bg-gray-50 rounded-xl p-8 border border-gray-200 hover:border-teal-400 transition-all">
                  <Icon className="w-10 h-10 text-teal-600 mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{u.title}</h3>
                  <p className="text-gray-600 text-sm mb-5 leading-relaxed">{u.description}</p>
                  <ul className="space-y-2">
                    {u.outcomes.map((o, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-gray-700">
                        <CheckCircle2 className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                        {o}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Case study */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Insurance AI Case Study</h2>
          <div className="bg-white rounded-xl p-8 border border-gray-200 shadow-sm">
            <div className="text-teal-600 text-xs font-bold uppercase tracking-widest mb-3">Insurance</div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">{caseStudy.title}</h3>
            <p className="text-gray-600 mb-6 leading-relaxed">{caseStudy.result}</p>
            <Link to={caseStudy.link} className="inline-flex items-center gap-1.5 text-teal-600 hover:text-teal-700 font-semibold group">
              Read Full Case Study <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Related links */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Related Services</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Generative AI Consulting', href: '/generative-ai-consulting' },
              { label: 'AI Implementation Services', href: '/ai-implementation-services' },
              { label: 'AI for Healthcare', href: '/ai-consulting-healthcare' },
              { label: 'AI for Logistics', href: '/ai-consulting-logistics' },
              { label: 'Insurance Automation Case Study', href: '/case-studies/insurance-automation' },
              { label: 'Payment Automation Case Study', href: '/case-studies/payment-automation' },
              { label: 'AI Consulting India', href: '/ai-consulting-india' },
              { label: 'All Case Studies', href: '/case-studies' },
            ].map((link, i) => (
              <Link key={i} to={link.href} className="flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium text-sm group">
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-indigo-600 to-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Automate Insurance Operations with AI?
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Tell us your claims, underwriting, or fraud challenge. We'll scope an AI solution in 48 hours.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-white text-teal-700 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-all text-lg"
          >
            Talk to an Insurance AI Consultant
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </>
  );
};

export default AIConsultingInsurance;
